import { ref, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetShopDocument,
  type GetShopQuery,
  ListShopForTransPickupRequestDocument,
  type ListShopForTransPickupRequestQuery,
  type ShopListForTransPickupRequestInput,
  type TransPickupRequestCreateInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import type { ShopOption, ShopResponse, DeliveryAvailabilityData, DeliveryArea } from "../types";

/** 店舗選択・住所表示に必要な依存をまとめた型。 */
interface UseDeliveryMoveRegisterAddressParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  transPickupRequestCreateInput: TransPickupRequestCreateInput;
  shopFromListItems: Ref<ShopOption[]>;
  deliveryAreaName: Ref<string>;
  display_delivery_time: Ref<string>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: DeliveryAvailabilityData[][];
  disp_deliveryAvailabilityData: DeliveryAvailabilityData[][];
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartHourItem: number[];
  deliveryStartHourValues: Ref<number | null | undefined>;
  deliveryStartMinitValues: Ref<{ label: string; deliveryStartMinit: number } | null | undefined>;
  listDeliveryArea: Ref<DeliveryArea[] | null>;
  doubleDeliOriconCount: Ref<number | null | undefined>;
  charterDeliOriconCount: Ref<number | null | undefined>;
  getAreaList: (address: string) => Promise<void>;
}

/** 配達元/配達先店舗選択、店舗入れ替えを扱う composable。 */
export const useDeliveryMoveRegisterAddress = (params: UseDeliveryMoveRegisterAddressParams) => {
  const {
    errdialog,
    errMessage,
    transPickupRequestCreateInput,
    shopFromListItems,
    deliveryAreaName,
    display_delivery_time,
    deliveryAvailabilityHeader,
    deliveryAvailabilityData,
    disp_deliveryAvailabilityData,
    deliveryId_0_Flg,
    deliveryStartHourItem,
    deliveryStartHourValues,
    deliveryStartMinitValues,
    listDeliveryArea,
    doubleDeliOriconCount,
    charterDeliOriconCount,
    getAreaList,
  } = params;

  // 店舗(配達先)
  const shopToListItems = ref<ShopOption[]>([]);

  const shopSelectValues_from = ref<ShopOption>({ shop_code: '', name: '' });
  const shopSelectValues_to = ref<ShopOption>({ shop_code: '', name: '' });

  // 店舗情報
  const shopData = ref<ShopResponse | null>(null);
  const shopData_from_address = ref('');
  const shopData_from_address1 = ref('');
  const shopData_to_address = ref('');
  const shopData_to_address1 = ref('');

  // 配達先店舗一覧 input
  const shopToListInput: ShopListForTransPickupRequestInput = {
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    to_shop_code: '',
    from_shop_code: ''
  };

  // 配送元選択時の処理（v-autocompleteの@update:model-valueから呼び出される）
  function onUpdateShopFrom(newValue: ShopOption | null): void {
    if (newValue && newValue.shop_code !== shopSelectValues_from.value.shop_code) {
      // ディープコピーしてread-only問題を回避
      shopSelectValues_from.value = { ...newValue };
      onChangeShopFrom();
    } else if (!newValue) {
      // クリアされた場合
      shopSelectValues_from.value = { shop_code: '', name: '' };
      onChangeShopFrom();
    }
  }

  // 配送先選択時の処理（v-autocompleteの@update:model-valueから呼び出される）
  function onUpdateShopTo(newValue: ShopOption | null): void {
    if (newValue && newValue.shop_code !== shopSelectValues_to.value.shop_code) {
      // ディープコピーしてread-only問題を回避
      shopSelectValues_to.value = { ...newValue };
      onChangeShopTo();
    } else if (!newValue) {
      // クリアされた場合
      shopSelectValues_to.value = { shop_code: '', name: '' };
      onChangeShopTo();
    }
  }

  // 配達店舗入れ替えボタンクリック
  async function onClickShopChangeBtn(): Promise<void> {
    // 配達店舗入れ替え
    const befor_Select_from = { ...shopSelectValues_from.value };
    const befor_Address_from = shopData_from_address.value;
    const befor_Address1_from = shopData_from_address1.value;

    shopSelectValues_from.value = { ...shopSelectValues_to.value };
    shopData_from_address.value = shopData_to_address.value;
    shopData_from_address1.value = shopData_to_address1.value;
    shopSelectValues_to.value = befor_Select_from;
    shopData_to_address.value = befor_Address_from;
    shopData_to_address1.value = befor_Address1_from;

    transPickupRequestCreateInput.ship_from_shop_code = shopSelectValues_from.value.shop_code;

    // 配送希望時間選択をクリア（カレンダーテーブルも含む）
    display_delivery_time.value = '';
    deliveryAreaName.value = '';
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.length = 0;
    disp_deliveryAvailabilityData.length = 0;
    listDeliveryArea.value = null;
    transPickupRequestCreateInput.area_code = '';
    transPickupRequestCreateInput.required_delivery_date = '';

    await onChangeShopTo();
  }

  // 配達元選択時 @change
  async function onChangeShopFrom(): Promise<void> {
    shopData_from_address.value = '';
    shopData_from_address1.value = '';
    deliveryAreaName.value = '';
    display_delivery_time.value = '';
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.length = 0;
    // 配達希望エリアと配送希望時間をクリア（カレンダーテーブルも含む）
    disp_deliveryAvailabilityData.length = 0;
    listDeliveryArea.value = null;
    transPickupRequestCreateInput.area_code = '';
    transPickupRequestCreateInput.required_delivery_date = '';

    // nullチェックを追加（v-autocompleteでクリアした場合にnullになる可能性がある）
    if (!shopSelectValues_from.value || shopSelectValues_from.value.shop_code === '') {
      return;
    }

    transPickupRequestCreateInput.ship_from_shop_code = shopSelectValues_from.value.shop_code;
    /*
    if (
      shopSelectValues_to.value.shop_code === transPickupRequestCreateInput.ship_from_shop_code
    ) {
      transPickupRequestCreateInput.ship_from_shop_code = '';
      shopSelectValues_from.value = { shop_code: '', name: '' };
      errdialog.value = true;
      errMessage.value = '配達元と配達先に異なる店舗を設定してください。';
      return;
    }
    */
    try {
      const apolloClient = await generateApolloClient();
      const shop_data: ApolloQueryResult<GetShopQuery> = await apolloClient.query<GetShopQuery>({
        query: GetShopDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: transPickupRequestCreateInput.ship_from_shop_code
        }
      });

      shopData.value = shop_data.data.getShop!;
      shopData_from_address.value =
        '〒' +
        shopData.value.zip_code +
        ' ' +
        shopData.value.address1 +
        shopData.value.address2 +
        ' ' +
        (shopData.value.address3 ? shopData.value.address3 : '');
      shopData_from_address1.value = shopData.value.address1;

      // nameプロパティを直接更新（オブジェクト全体を置き換えない）
      shopSelectValues_from.value.name = shopData.value.name;
      shopToListInput.to_shop_code = transPickupRequestCreateInput.ship_from_shop_code;

      doubleDeliOriconCount.value = shopData.value.double_deli_oricon_count;
      charterDeliOriconCount.value = shopData.value.charter_deli_oricon_count;

      // await getAreaList(shopData_to_address1.value);

      deliveryAvailabilityHeader.value = new Map();
      deliveryAvailabilityData.length = 0;
      disp_deliveryAvailabilityData.length = 0;
      deliveryId_0_Flg.value = false;
      deliveryStartHourItem.splice(0);
      deliveryStartHourValues.value = undefined;
      deliveryStartMinitValues.value = undefined;
      listDeliveryArea.value = null;
      transPickupRequestCreateInput.area_code = '';
      shopData_to_address.value = '';

      const listShopTo: ApolloQueryResult<ListShopForTransPickupRequestQuery> = await apolloClient.query<ListShopForTransPickupRequestQuery>({
        query: ListShopForTransPickupRequestDocument,
        variables: { input: shopToListInput }
      });
      // APIから返されるデータに重複がある場合があるため、shop_codeで重複を削除
      const shopToList = listShopTo.data.listShopForTransPickupRequest as ShopOption[] | null;
      if (shopToList) {
        const uniqueShopCodes = new Set<string>();
        shopToListItems.value = shopToList.filter(shop => {
          if (uniqueShopCodes.has(shop.shop_code)) {
            return false;
          }
          uniqueShopCodes.add(shop.shop_code);
          return true;
        });
      } else {
        shopToListItems.value = null;
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : '予期せぬエラーが発生しました。';
      return;
    }
  }

  // 配達先選択時 @change
  async function onChangeShopTo(): Promise<void> {
    shopData_to_address.value = '';
    shopData_to_address1.value = '';
    deliveryAreaName.value = '';
    display_delivery_time.value = '';
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.length = 0;
    // 配達希望エリアと配送希望時間をクリア（カレンダーテーブルも含む）
    disp_deliveryAvailabilityData.length = 0;
    listDeliveryArea.value = null;
    transPickupRequestCreateInput.area_code = '';
    transPickupRequestCreateInput.required_delivery_date = '';

    // nullチェックを追加（v-autocompleteでクリアした場合にnullになる可能性がある）
    if (!shopSelectValues_to.value || shopSelectValues_to.value.shop_code === '') {
      return;
    }

    transPickupRequestCreateInput.delivery_shop_code = shopSelectValues_to.value.shop_code;

    if (shopSelectValues_from.value.shop_code === transPickupRequestCreateInput.delivery_shop_code) {
      transPickupRequestCreateInput.delivery_shop_code = '';
      shopSelectValues_to.value = { shop_code: '', name: '' };
      errdialog.value = true;
      errMessage.value = '配達元と配達先に異なる店舗を設定してください。';
      return;
    }

    if (
      shopSelectValues_from.value.shop_code !== localStorageGetShopCode() &&
      transPickupRequestCreateInput.delivery_shop_code !== localStorageGetShopCode()
    ) {
      transPickupRequestCreateInput.delivery_shop_code = '';
      shopSelectValues_to.value = { shop_code: '', name: '' };
      errdialog.value = true;
      errMessage.value = '配達元と配達先のいずれかは自店舗を設定してください。';
      return;
    }

    try {
      const apolloClient = await generateApolloClient();
      const shop_data: ApolloQueryResult<GetShopQuery> = await apolloClient.query<GetShopQuery>({
        query: GetShopDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: shopSelectValues_to.value.shop_code
        }
      });
      shopData.value = shop_data.data.getShop!;
      shopData_to_address.value =
        '〒' +
        shopData.value.zip_code +
        ' ' +
        shopData.value.address1 +
        shopData.value.address2 +
        ' ' +
        (shopData.value.address3 ? shopData.value.address3 : '');
      shopData_to_address1.value = shopData.value.address1;
      shopSelectValues_to.value = {
        ...shopSelectValues_to.value,
        name: shopData.value.name
      };

      await getAreaList(shopData.value.address1);
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : '予期せぬエラーが発生しました。';
      return;
    }
  }

  return {
    shopToListItems,
    shopSelectValues_from,
    shopSelectValues_to,
    shopData,
    shopData_from_address,
    shopData_from_address1,
    shopData_to_address,
    shopData_to_address1,
    shopToListInput,
    onUpdateShopFrom,
    onUpdateShopTo,
    onClickShopChangeBtn,
  };
};
